import React, {
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import Image from 'next/image';
import { useMediaQuery } from '@mui/material';
import classnames from 'classnames';

import videoTour from 'public/images/video-tour.svg';
import floorplan from 'public/images/floorplan.svg';
import location from 'public/images/location.svg';

import dynamic from 'next/dynamic';

import { Position } from 'types';
import { BuildingPageContext } from 'pages/[urlParam]/[urlBuildParam]';
import { UnitPageContext } from 'pages/[urlParam]/[urlBuildParam]/[urlUnitParam]';
import { getBuildingFloorplans } from 'src/utils/buildingUtils';
import { Building } from 'apolloClient/types';
import { Listing } from 'apolloClient/types/Units';

import UnitBuildingLocation from '../UnitBuildingLocation';
import ImageCarousel from './ImageCarousel';

import { ProcessedImage } from 'lib/getImages';
import { translateUnitStatusBasic } from '../../lib/translateUnitStatusBasic';
import { RibbonLabelProps } from 'components/RibbonLabel';
import { RibbonContainer } from 'components/RibbonContainer';
import PopUpUnitBuildingV2 from 'componentsV2/PopUpUnitBuildingV2';

const PopUpViewLargePhoto = dynamic(() => import('./PopUpViewLargePhoto'), {
  loading: () => null,
  ssr: false,
});

type BuildingCarouselProps = {
  images?: ProcessedImage[];
  position?: Position;
  renderHeader: (
    onClose: () => void,
    photoCounter?: string
  ) => React.JSX.Element;
  tours?: string[] | string;
  nameButton: string;
  carouselKey?: string;
};

export interface BuildingCarouselHandle {
  openDialog: (tab: number) => void;
}

const getTourLabel = (tourUrl?: string | string[] | null) => {
  if (!tourUrl) return null;
  if (Array.isArray(tourUrl)) return 'video';
  if (
    tourUrl?.includes('youtube') ||
    tourUrl?.includes('youtu.be') ||
    tourUrl?.includes('vimeo')
  )
    return 'video tour';

  return 'virtual tour';
};

const initialPhotosModalState = {
  isOpen: false,
  initialShowedIndex: 0,
};

const BuildingCarousel = React.forwardRef<
  BuildingCarouselHandle,
  BuildingCarouselProps
>(function BuildingCarousel(
  { images, position, renderHeader, tours, nameButton, carouselKey },
  ref
) {
  const [photosModalState, setPhotosModalState] = useState(
    initialPhotosModalState
  );
  const [isBuildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [selectedDialogTab, setSelectedDialogTab] = useState<number>(0);

  useImperativeHandle(ref, () => ({
    openDialog: (tab: number) => openBuildingDialog(tab),
  }));

  const ribbons = [] as RibbonLabelProps[];

  const isMobile = useMediaQuery('(max-width: 767px)');

  const { building: buildingData } = useContext(BuildingPageContext) || {};
  const { building, listing } = useContext(UnitPageContext) || {};
  const {
    statusCode,
    propertyTypeCode,
    closeDate,
    yearBuilt,
    isShortSale,
    isReo,
  } = listing || {};
  const isPreconstruction = buildingData?.isPreconstruction;
  const floorplansFromBuilding = getBuildingFloorplans(
    buildingData?.floorplans
  );
  const floorplanFromListing = listing?.floorplan;
  const floorPlanExists =
    Boolean(floorplansFromBuilding && floorplansFromBuilding.length > 0) ||
    Boolean(floorplanFromListing);
  const sitePlanExists = Boolean(
    (buildingData?.floorPlanImage || building?.floorPlanImage)?.data
  );
  const imagesExists = Boolean(images && images.length > 0);
  const isTourExists = Boolean((tours?.length || 0) > 0);
  const isPropertypanoramaTour = tours?.includes('propertypanorama');

  const unitLabels =
    propertyTypeCode &&
    statusCode &&
    translateUnitStatusBasic(propertyTypeCode, statusCode, closeDate);

  const isUnitPreconstruction =
    building?.isPreconstruction || Number(yearBuilt) > new Date().getFullYear();

  const extraUnitLabels = (() => {
    if (!building?.isPreconstruction && isReo) {
      return `Foreclosure`;
    } else if (!building?.isPreconstruction && isShortSale) {
      return `Short sale`;
    } else if (isUnitPreconstruction) {
      return `Pre-construction`;
    } else {
      return null;
    }
  })();

  const isUnitLabelLongName = statusCode === 'R' || statusCode === 'CS';

  function closePhotosModal() {
    setPhotosModalState(initialPhotosModalState);
  }

  const openBuildingDialog = (tab: number): void => {
    closePhotosModal();
    setBuildingDialogOpen(true);
    setSelectedDialogTab(tab);
  };

  // Add ribbons based on conditions
  if (isPreconstruction) {
    ribbons.push({
      label: 'Pre-construction',
      type: 'biancone',
    });
  }
  if (unitLabels) {
    ribbons.push({
      label: unitLabels,
      type: 'golden',
      statusCode: statusCode as 'R' | 'CS',
    });
  }
  if (extraUnitLabels) {
    ribbons.push({
      label: extraUnitLabels,
      type: 'biancone',
      isUnitPreconstruction,
    });
  }

  const tabsAmount = useMemo(() => {
    return Object.values({
      photos: !imagesExists,
      sitePlan: !floorPlanExists,
      floorPlan: !floorPlanExists,
      tour: !isTourExists,
      directions: !position,
    }).length;
  }, [imagesExists, floorPlanExists, isTourExists, position]);

  return (
    <>
      <section className="px-0 mt-[21px] md:mt-5 lg:mt-[10px] text-14-26-0.3 xxxl:container lg:pt-1 lg:pb-2">
        <div className="relative">
          <RibbonContainer ribbons={ribbons} isMobile={isMobile} />

          {imagesExists ? (
            <ImageCarousel
              images={images}
              onImageClick={() => {
                setSelectedDialogTab(0);
                setBuildingDialogOpen(true);
              }}
              gap={5}
              carouselKey={carouselKey}
            />
          ) : (
            <div className="z-50 h-[220px] md:h-88 lg:h-112">
              <UnitBuildingLocation position={position} />
            </div>
          )}
          <div className="absolute w-full -translate-x-1/2 md:left-auto md:w-96 transform-none bottom-[-43px] md:bottom-4 lg:bottom-auto md:right-20 lg:top-8 z-10">
            <div className="container z-40 flex justify-center w-auto px-6 pt-0 space-x-2 outline-none md:pl-10 md:space-x-0">
              {isTourExists && !isPropertypanoramaTour && (
                <button
                  onClick={() => openBuildingDialog(tabsAmount - 1)}
                  className={`${
                    (floorPlanExists || position) && 'md:border-r-0'
                  } z-30 flex items-center justify-center min-w-[120px] w-auto md:min-w-[150px] h-[28px] xl:h-[34px] px-2 py-1 uppercase bg-white border-2 md:w-40 hover-animation group border-gold-dark md:px-3 md:space-x-2 xl:pb-[7px] xl:pt-[5px]`}
                >
                  <span className="hidden hover-animation group-hover:opacity-70 md:inline-block pt-[5px]">
                    <Image src={videoTour} alt="videoTour" />
                  </span>
                  <span className="hover-animation whitespace-nowrap group-hover:opacity-70 text-12-21-0.3 md:text-14-21-0.3">
                    {getTourLabel(tours)}
                  </span>
                </button>
              )}
              {(floorPlanExists || sitePlanExists) && (
                <button
                  type="button"
                  onClick={() => openBuildingDialog(floorPlanExists ? 2 : 1)}
                  className={classnames(
                    'flex items-center justify-center min-w-[111px] md:min-w-[153px] px-2 py-1 h-[28px] xl:h-[34px] xl:pb-[7px] xl:pt-[5px] uppercase bg-white border-2 md:w-36 hover-animation group border-gold-dark md:px-3 md:space-x-2',
                    {
                      'md:border-r-0': position,
                    }
                  )}
                >
                  <div className="hidden pt-[5px] hover-animation group-hover:opacity-70 md:inline-block">
                    <Image src={floorplan} alt="floorplans" />
                  </div>
                  <span className="hover-animation group-hover:opacity-70 text-12-21-0.3 md:text-14-14-0.3">
                    {nameButton}
                  </span>
                </button>
              )}
              {position && (
                <button
                  onClick={() => openBuildingDialog(3)}
                  className="flex items-center justify-center min-w-[91px] md:min-w-[125px] lg:min-w-[135px] px-2 py-1 h-[28px] xl:h-[34px] xl:pb-[7px] xl:pt-[5px] uppercase bg-white border-2 md:w-36 hover-animation group border-gold-dark md:px-0 md:space-x-2"
                >
                  <div className="hidden pt-[5px] h-[28px] w-[18px] hover-animation group-hover:opacity-70 md:inline-block">
                    <Image
                      src={location}
                      alt="location"
                      height={20}
                      width={20}
                    />
                  </div>
                  <div className="hover-animation group-hover:opacity-70 text-12-21-0.3 md:text-14-14-0.3 lg:!ml-[6px]">
                    Location
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className="md:container mt-[58px] md:mt-20 text-m md:hidden">
        <div className="w-fill h-[1px] bg-beige" />
      </section>
      {photosModalState.isOpen && (
        <PopUpViewLargePhoto
          open={photosModalState.isOpen}
          onClose={closePhotosModal}
          images={images?.map((image) => image.originalImage)}
          actionHandle={() => openBuildingDialog(0)}
          renderHeader={(photoCounter) =>
            renderHeader(closePhotosModal, photoCounter)
          }
          initialActiveIndex={photosModalState.initialShowedIndex}
        />
      )}
      {isBuildingDialogOpen && (
        <PopUpUnitBuildingV2
          entityType={listing ? 'listing' : 'building'}
          entity={(buildingData as Building) || (listing as Listing)}
          toursButtonLabel={getTourLabel(tours)}
          floorplanButtonLabel={listing ? 'Floor plan' : 'Floor plans'}
          open={isBuildingDialogOpen}
          onClose={() => setBuildingDialogOpen(false)}
          openPopupLarge={setPhotosModalState}
          activeTab={selectedDialogTab}
          images={images?.map((image) => image.originalImage) || []}
          tours={tours}
          position={position}
          tabsRenderExclude={{
            photos: !imagesExists,
            floorPlan: !floorPlanExists,
            tour: !isTourExists,
            directions: !position,
            sitePlan: !sitePlanExists,
          }}
          renderHeader={renderHeader}
          initialActiveRowIndex={0}
          buildingFloorPlanImage={
            listing ? building?.floorPlanImage : undefined
          }
        />
      )}
    </>
  );
});

export default BuildingCarousel;
